// Footer — bilingual company name, site nav, and a clearly-marked PLACEHOLDER
// contact line (real address/phone/email are TBD per plan §12.6; the keys are
// explicitly placeholder-flagged so they're obvious to swap later).
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { SUPPORTED_LOCALES, type AppLocale, DEFAULT_LOCALE } from "../i18n";

function isLocale(v: string | undefined): v is AppLocale {
  return !!v && (SUPPORTED_LOCALES as readonly string[]).includes(v);
}

const NAV_SEGMENTS = ["", "about", "services", "network", "partners", "contact"] as const;

export default function Footer() {
  const { lang } = useParams();
  const { t } = useTranslation("common");

  const current: AppLocale = isLocale(lang) ? lang : DEFAULT_LOCALE;

  return (
    <footer className="mt-auto border-t border-[var(--brand-tint)] bg-[var(--brand-tint-3)]/40">
      <div className="mx-auto grid w-full max-w-6xl gap-10 px-6 py-14 md:grid-cols-[1.5fr_1fr_1.5fr]">
        <div>
          <p className="font-zh text-[var(--text-lg)] font-bold leading-snug text-[var(--ink)]">
            {t("brand.name")}
          </p>
          <p className="mt-3 text-[var(--text-base)] text-[var(--brand-slate-text)]">
            {t("footer.tagline")}
          </p>
        </div>

        <nav aria-label={t("footer.nav")}>
          <p className="font-sans text-[var(--text-sm)] font-semibold uppercase tracking-[0.15em] text-[var(--brand-azure-text)]">
            {t("footer.nav")}
          </p>
          <ul className="mt-4 space-y-2.5">
            {NAV_SEGMENTS.map((seg) => (
              <li key={seg || "home"}>
                <Link
                  to={`/${current}${seg ? `/${seg}` : ""}`}
                  className="text-[var(--text-base)] text-[var(--ink)]/80 transition-colors hover:text-[var(--brand-azure-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-azure)]"
                >
                  {t(`nav.${seg || "home"}`)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <p className="font-sans text-[var(--text-sm)] font-semibold uppercase tracking-[0.15em] text-[var(--brand-azure-text)]">
            {t("footer.contactHeading")}
          </p>
          <dl className="mt-4 space-y-3 text-[var(--text-base)]">
            {/* Only render rows with a real value. i18n has returnEmptyString:false, so
                an empty value (fields still 待补充, e.g. phone/email) makes t() echo the
                KEY back — detect that (value === key) and skip the row. */}
            {([
              ["footer.addressLabel", "footer.address"],
              ["footer.phoneLabel", "footer.phone"],
              ["footer.emailLabel", "footer.email"],
            ] as const)
              .map(([labelKey, valueKey]) => ({ labelKey, valueKey, value: t(valueKey) }))
              .filter(({ valueKey, value }) => value && value !== valueKey)
              .map(({ labelKey, value }) => (
                <div key={labelKey} className="flex gap-2">
                  <dt className="shrink-0 text-[var(--brand-slate-text)]">{t(labelKey)}</dt>
                  <dd className="text-[var(--ink)]/80">{value}</dd>
                </div>
              ))}
          </dl>
        </div>
      </div>

      <div className="border-t border-[var(--brand-tint)]">
        <div className="mx-auto w-full max-w-6xl px-6 py-5">
          <small className="text-[var(--text-sm)] text-[var(--brand-slate-text)]">
            © {new Date().getFullYear()} {t("brand.name")} · {t("footer.rights")}
          </small>
        </div>
      </div>
    </footer>
  );
}
